import React, { useMemo } from "react";
import { Text, useMantineTheme, Box } from "@mantine/core";
import { FormSectionCard } from "../../layout/page";

const GAUGE_CHART_HEIGHT = 200;

const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
  const angleInRadians = ((angleInDegrees - 180) * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

const describeArc = (x, y, radius, startAngle, endAngle) => {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
};

const GaugeChart = ({
  title,
  value = 0,
  max = 100,
  formatValue,
  formatMin,
  formatMax,
  color,
  trackColor,
  height = GAUGE_CHART_HEIGHT,
  compact = false,
}) => {
  const theme = useMantineTheme();
  const safeMax = max > 0 ? max : 1;
  const ratio = Math.min(Math.max((Number(value) || 0) / safeMax, 0), 1);
  const width = 280;
  const centerX = width / 2;
  const centerY = height - 28;
  const radius = 88;
  const strokeWidth = 18;
  const startAngle = 0;
  const endAngle = 180;
  const valueAngle = startAngle + ratio * (endAngle - startAngle);

  const fillColor = color || theme.colors.deepBlue?.[6] || "#1971c2";
  const bgTrackColor =
    trackColor ||
    (theme.colorScheme === "dark"
      ? theme.colors.gray?.[7] || "#495057"
      : theme.colors.gray?.[2] || "#e9ecef");
  const labelColor =
    theme.colorScheme === "dark"
      ? theme.colors.gray?.[1]
      : theme.colors.dark?.[7];

  const backgroundPath = useMemo(
    () => describeArc(centerX, centerY, radius, startAngle, endAngle),
    [centerX, centerY, radius]
  );
  const valuePath = useMemo(
    () =>
      ratio > 0
        ? describeArc(centerX, centerY, radius, startAngle, valueAngle)
        : "",
    [centerX, centerY, radius, ratio, valueAngle]
  );

  const displayValue = formatValue
    ? formatValue(value)
    : Number(value || 0).toLocaleString();
  const minLabel = formatMin ? formatMin(0) : "0";
  const maxLabel = formatMax ? formatMax(max) : String(max);
  const minX = polarToCartesian(centerX, centerY, radius, startAngle).x;
  const maxX = polarToCartesian(centerX, centerY, radius, endAngle).x;
  const endpointLabelY = centerY + strokeWidth / 2 + 22;

  return (
    <FormSectionCard
      title={title}
      h="100%"
      mb={compact ? 0 : undefined}
      p={compact ? 0 : undefined}
      paperPadding={compact ? "sm" : "xl"}
      titleMb={compact ? 4 : "xs"}
    >
      <Box sx={{ overflow: "visible", pb: 2 }}>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{ overflow: "visible" }}
      >
        <path
          d={backgroundPath}
          fill="none"
          stroke={bgTrackColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {valuePath ? (
          <path
            d={valuePath}
            fill="none"
            stroke={fillColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
        ) : null}
        <text
          x={centerX}
          y={centerY - 14}
          textAnchor="middle"
          fontSize="22"
          fontWeight="600"
          fill={labelColor}
        >
          {displayValue}
        </text>
        <text
          x={minX}
          y={endpointLabelY}
          textAnchor="middle"
          fontSize="11"
          fill={labelColor}
        >
          {minLabel}
        </text>
        <text
          x={maxX}
          y={endpointLabelY}
          textAnchor="middle"
          fontSize="11"
          fill={labelColor}
        >
          {maxLabel}
        </text>
      </svg>
      </Box>
    </FormSectionCard>
  );
};

export default GaugeChart;
export { GAUGE_CHART_HEIGHT };
